import { faPen, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons"
import { Loading, Modal, Pagination, TextArea, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"
import { GET_ITEM_LIMIT } from "constants/constants"
import useCommon from "hooks/useCommon"
import { useMemo, useState } from "react"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import Select from "react-select"
import {
  AssetDetailRecord,
  useCreateAssetDetailMutation,
  useDeleteAssetDetailMutation,
  useListAssetCategoriesQuery,
  useListAssetDetailsQuery,
  useListAssetSubcategoriesQuery,
  useUpdateAssetDetailMutation,
} from "services/aimsBackend"

export default function AssetDetailsTab() {
  const { showSnackBar } = useCommon()

  const { data: items = [], isFetching } = useListAssetDetailsQuery()
  const { data: categories = [] } = useListAssetCategoriesQuery()
  const { data: subcategories = [] } = useListAssetSubcategoriesQuery()
  const [createDetail, { isLoading: isCreating }] = useCreateAssetDetailMutation()
  const [updateDetail, { isLoading: isUpdating }] = useUpdateAssetDetailMutation()
  const [deleteDetail, { isLoading: isDeleting }] = useDeleteAssetDetailMutation()

  const categoryOptions = useMemo(() => categories.map(c => ({ value: c.id, label: c.title })), [categories])

  // Filters
  const [search, setSearch] = useState("")
  const [categoryFilter, setCategoryFilter] = useState<{ value: number; label: string } | null>(null)
  const [subcategoryFilter, setSubcategoryFilter] = useState<{ value: number; label: string } | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(GET_ITEM_LIMIT)

  const subcategoryFilterOptions = useMemo(() => {
    if (!categoryFilter) return subcategories.map(s => ({ value: s.id, label: s.title }))
    return subcategories.filter(s => s.category.id === categoryFilter.value).map(s => ({ value: s.id, label: s.title }))
  }, [categoryFilter, subcategories])

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false)
  const [addTitle, setAddTitle] = useState("")
  const [addError, setAddError] = useState("")
  const [addCategoryId, setAddCategoryId] = useState<number | null>(null)
  const [addSubcategoryId, setAddSubcategoryId] = useState<number | null>(null)
  const [addSubcategoryError, setAddSubcategoryError] = useState("")

  const addSubcategoryOptions = useMemo(() => {
    if (!addCategoryId) return subcategories.map(s => ({ value: s.id, label: s.title }))
    return subcategories.filter(s => s.category.id === addCategoryId).map(s => ({ value: s.id, label: s.title }))
  }, [addCategoryId, subcategories])

  // Edit modal
  const [editItem, setEditItem] = useState<AssetDetailRecord | null>(null)
  const [editTitle, setEditTitle] = useState("")
  const [editError, setEditError] = useState("")
  const [editCategoryId, setEditCategoryId] = useState<number | null>(null)
  const [editSubcategoryId, setEditSubcategoryId] = useState<number | null>(null)
  const [editSubcategoryError, setEditSubcategoryError] = useState("")

  const editSubcategoryOptions = useMemo(() => {
    if (!editCategoryId) return subcategories.map(s => ({ value: s.id, label: s.title }))
    return subcategories.filter(s => s.category.id === editCategoryId).map(s => ({ value: s.id, label: s.title }))
  }, [editCategoryId, subcategories])

  const [deleteTarget, setDeleteTarget] = useState<AssetDetailRecord | null>(null)

  const filteredRows = items.filter(item => {
    const matchesSearch = !search || item.title.toLowerCase().includes(search.toLowerCase())
    const matchesCategory = !categoryFilter || item.subcategory.category.id === categoryFilter.value
    const matchesSubcategory = !subcategoryFilter || item.subcategory.id === subcategoryFilter.value
    return matchesSearch && matchesCategory && matchesSubcategory
  })

  const pagedRows = filteredRows.slice(
    pageNumber === 1 ? 0 : (pageNumber - 1) * pageSize,
    (pageNumber - 1) * pageSize + pageSize,
  )

  const formatApiError = (err: any, fallback: string) => {
    const data = err?.data
    if (typeof data === "string") return data
    if (data && typeof data === "object") {
      return Object.entries(data)
        .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
        .join("; ")
    }
    return fallback
  }

  const handleAdd = async () => {
    let hasError = false
    if (!addTitle.trim()) {
      setAddError("Title is required")
      hasError = true
    }
    if (!addSubcategoryId) {
      setAddSubcategoryError("Subcategory is required")
      hasError = true
    }
    if (hasError) return

    try {
      await createDetail({ title: addTitle.trim(), subcategory_id: addSubcategoryId! }).unwrap()
      showSnackBar("Asset detail added successfully", "success")
      setAddTitle("")
      setAddCategoryId(null)
      setAddSubcategoryId(null)
      setAddError("")
      setAddSubcategoryError("")
      setShowAddModal(false)
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to create asset detail"), "error")
    }
  }

  const handleEdit = async () => {
    let hasError = false
    if (!editTitle.trim()) {
      setEditError("Title is required")
      hasError = true
    }
    if (!editSubcategoryId) {
      setEditSubcategoryError("Subcategory is required")
      hasError = true
    }
    if (hasError || !editItem) return

    try {
      await updateDetail({
        id: editItem.id,
        body: { title: editTitle.trim(), subcategory_id: editSubcategoryId! },
      }).unwrap()
      showSnackBar("Asset detail updated successfully", "success")
      setEditItem(null)
      setEditTitle("")
      setEditCategoryId(null)
      setEditSubcategoryId(null)
      setEditError("")
      setEditSubcategoryError("")
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to update asset detail"), "error")
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteDetail(deleteTarget.id).unwrap()
      showSnackBar("Asset detail deleted successfully", "success")
    } catch (err) {
      showSnackBar(formatApiError(err, "Failed to delete asset detail"), "error")
    } finally {
      setDeleteTarget(null)
    }
  }

  const columns: Array<ColumnShape<AssetDetailRecord>> = [
    { key: "title", dataKey: "title", title: "Title", width: 250, flexGrow: 1 },
    {
      key: "category",
      title: "Category",
      width: 150,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => rowData.subcategory?.category?.title || "",
    },
    {
      key: "subcategory",
      title: "Subcategory",
      width: 180,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => rowData.subcategory?.title || "",
    },
    {
      key: "action",
      title: "",
      width: 260,
      cellRenderer: ({ rowData }) => (
        <div className="d-flex">
          <Button
            action={() => {
              setEditItem(rowData)
              setEditTitle(rowData.title)
              setEditCategoryId(rowData.subcategory.category.id)
              setEditSubcategoryId(rowData.subcategory.id)
              setEditError("")
              setEditSubcategoryError("")
            }}
            label="Edit"
            icon={faPen}
            className="btn-full blue action-btn"
          />
          <Button
            action={() => setDeleteTarget(rowData)}
            label="Delete"
            icon={faTrash}
            className="btn-full danger action-btn ml-2"
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <div className="mt-3">
        <div className="d-flex align-items-center mb-3" style={{ gap: 12 }}>
          <Loading active={isFetching || isDeleting} />
          <div style={{ flex: 1, maxWidth: 250 }}>
            <TextInput
              labelText="Search"
              hideLabel
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPageNumber(1)
              }}
              placeholder="Search asset details..."
            />
          </div>
          <div style={{ width: 200 }}>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={categoryOptions}
              value={categoryFilter}
              onChange={option => {
                setCategoryFilter(option)
                setSubcategoryFilter(null)
                setPageNumber(1)
              }}
              isClearable
              placeholder="Filter by category"
            />
          </div>
          <div style={{ width: 200 }}>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={subcategoryFilterOptions}
              value={subcategoryFilter}
              onChange={option => {
                setSubcategoryFilter(option)
                setPageNumber(1)
              }}
              isClearable
              placeholder="Filter by subcategory"
            />
          </div>
          <Button
            action={() => {
              setAddTitle("")
              setAddCategoryId(null)
              setAddSubcategoryId(null)
              setAddError("")
              setAddSubcategoryError("")
              setShowAddModal(true)
            }}
            label="Add asset detail"
            className="btn-full green"
            icon={faPlus}
          />
        </div>

        <div style={{ height: "100%", minHeight: "60vh", marginTop: 10 }}>
          <AutoResizer>
            {({ width, height }) => (
              <BaseTable
                data={pagedRows}
                width={width}
                height={height}
                rowKey="id"
                className="custom-table"
                rowHeight={38}
                headerHeight={45}
                columns={columns}
                emptyRenderer={() => <p>No records to display</p>}
              />
            )}
          </AutoResizer>
        </div>

        <Pagination
          totalItems={filteredRows.length}
          pageSize={pageSize}
          pageSizes={[10, 20, 30]}
          page={pageNumber}
          onChange={(event: any): void => {
            setPageNumber((event as any).page)
            setPageSize((event as any).pageSize)
          }}
        />
      </div>

      {/* Add Modal */}
      <Modal
        open={showAddModal}
        modalHeading="Add Asset Detail"
        primaryButtonText={isCreating ? "Adding..." : "Add"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowAddModal(false)}
        onSecondarySubmit={() => setShowAddModal(false)}
        onRequestSubmit={handleAdd}
        primaryButtonDisabled={isCreating}
      >
        <TextArea
          labelText="Title"
          value={addTitle}
          onChange={e => {
            setAddTitle(e.target.value)
            if (addError) setAddError("")
          }}
          placeholder="Enter asset detail title"
          invalid={!!addError}
          invalidText={addError}
          rows={4}
        />
        <div className="mt-2">
          <div className="wfp--form-item">
            <label className="wfp--label">Category</label>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={categoryOptions}
              value={categoryOptions.find(opt => opt.value === addCategoryId) || null}
              onChange={option => {
                setAddCategoryId(option?.value || null)
                setAddSubcategoryId(null)
              }}
              placeholder="Select category to filter subcategories"
              isClearable
            />
          </div>
        </div>
        <div className="mt-2">
          <div className="wfp--form-item">
            <label className="wfp--label">Subcategory</label>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={addSubcategoryOptions}
              value={addSubcategoryOptions.find(opt => opt.value === addSubcategoryId) || null}
              onChange={option => {
                setAddSubcategoryId(option?.value || null)
                if (addSubcategoryError) setAddSubcategoryError("")
              }}
              placeholder="Select subcategory"
              styles={addSubcategoryError ? { control: base => ({ ...base, borderColor: "#da1e28" }) } : {}}
            />
            {addSubcategoryError && <div className="wfp--form-requirement">{addSubcategoryError}</div>}
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editItem}
        modalHeading="Edit Asset Detail"
        primaryButtonText={isUpdating ? "Saving..." : "Save"}
        secondaryButtonText="Cancel"
        onRequestClose={() => setEditItem(null)}
        onSecondarySubmit={() => setEditItem(null)}
        onRequestSubmit={handleEdit}
        primaryButtonDisabled={isUpdating}
      >
        <TextArea
          labelText="Title"
          value={editTitle}
          onChange={e => {
            setEditTitle(e.target.value)
            if (editError) setEditError("")
          }}
          placeholder="Enter asset detail title"
          invalid={!!editError}
          invalidText={editError}
          rows={4}
        />
        <div className="mt-2">
          <div className="wfp--form-item">
            <label className="wfp--label">Category</label>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={categoryOptions}
              value={categoryOptions.find(opt => opt.value === editCategoryId) || null}
              onChange={option => {
                setEditCategoryId(option?.value || null)
                setEditSubcategoryId(null)
              }}
              placeholder="Select category to filter subcategories"
              isClearable
            />
          </div>
        </div>
        <div className="mt-2">
          <div className="wfp--form-item">
            <label className="wfp--label">Subcategory</label>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={editSubcategoryOptions}
              value={editSubcategoryOptions.find(opt => opt.value === editSubcategoryId) || null}
              onChange={option => {
                setEditSubcategoryId(option?.value || null)
                if (editSubcategoryError) setEditSubcategoryError("")
              }}
              placeholder="Select subcategory"
              styles={editSubcategoryError ? { control: base => ({ ...base, borderColor: "#da1e28" }) } : {}}
            />
            {editSubcategoryError && <div className="wfp--form-requirement">{editSubcategoryError}</div>}
          </div>
        </div>
      </Modal>

      <DeleteConfirmationModal
        open={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={handleDelete}
      >
        Are you sure you want to delete &quot;{deleteTarget?.title}&quot;?
      </DeleteConfirmationModal>
    </>
  )
}
